/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Label from '../Label/Label';
import Textbox from '../Textbox/Textbox';
import Checkbox from '../Checkbox/Checkbox';
import Button from '../Button/Button';
import Service from '../Util/Service';
import UpIcon from './baseline_arrow_upward_black_24dp.png';
import DownIcon from './baseline_arrow_downward_black_24dp.png';
import $ from '../Util/Util';
import './Entitytypeview.css';

/**
 * Represents the entity type view.
 */

export default class Entitytypeview extends Component {

    /**
     * Properties.
     */

    static propTypes = {
        context:            PropTypes.object.isRequired,
        onLoading:          PropTypes.func.isRequired,
        onClose:            PropTypes.func.isRequired,
        onDelete:           PropTypes.func,
        entitytype:         PropTypes.object
    }

    /**
     * Default state.
     */

    state = {
        edit:               this.props.entitytype.id === undefined,
        groups:             [],
        attributes:         [],
        primaryattribute:   null
    }

    /**
     * Event that fires when the user wants to modify the current entity type.
     */

    _modify = () => {

        // Setting the maintenance mode and refreshing the screen
        
        this.setState({ edit: true }, () => {
            this._refresh();
        });
    }

    /**
     * Event that fires when the user wants to delete the current entity type.
     */

    _delete = () => {
        this.props.onDelete(this.props.entitytype);
    }

    /**
     * Event that fires when the user cancels a maintenance.
     */

    _cancel = () => {

        // Closing the entity type view

        this.props.onClose();
    }

    /**
     * Saves the entitytype.
     */

    _save = () => {

        // Validating name

        if (this.name.isEmpty()) {

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.entitytype.validation.missingname, error: true });

            return;
        }
        
        // Preparing

        let entitytype = {
            name:                   this.name.getValue(),
            entitycreateaccess:     [],
            attributes:             {}
        };

        // Storing the entity type id

        if (this.props.entitytype.id) {
            entitytype.id = this.props.entitytype.id;
        }

        // Storing the entity creation access rights

        this.entitycreateaccess.forEach((item) => {
            if (item.checkbox && item.checkbox.getValue()) {
                entitytype.entitycreateaccess.push(item.id);
            }
        });

        // Getting the attributes of the entity type

        let hasPrimary = false;

        this.state.attributes.forEach((attr) => {
            if (this['attr' + attr.id].getValue()) {
                if (this['primary' + attr.id].getValue()) {
                    hasPrimary = true;
                }

                entitytype.attributes[attr.id] = { 
                    primary:    this['primary' + attr.id].getValue(),
                    mandatory:  this['mandatory' + attr.id].getValue(),
                    multivalue: this['multivalue' + attr.id].getValue(),
                    ordernr:    attr.ordernr
                };
            }
        });

        // Checking attributes

        if (Object.keys(entitytype.attributes).length === 0) {
                        
            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.entitytype.validation.missingattributes, error: true });

            return;
        }

        // Checking primary attribute

        if (!hasPrimary) {

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.entitytype.validation.missingprimaryattribute, error: true });

            return;
        }

        // Showing progress indicator

        this.props.onLoading(true);

        // Saving the entity type

        Service.saveEntitytype(entitytype).then(() => {

            // Hiding progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.entitytype.savedmessage });

            // Closing the view

            this.props.onClose(true);
        }).catch((error) => {

            // Hiding progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /**
     * Refreshing the screen based on the entity type.
     */

    _refresh = () => {

        // Setting the name of the entity type

        this.name.setValue(this.props.entitytype.name);

        // Does any attributes specified of the entity type?

        if (this.props.entitytype.attributes) {
            for (let i = 0; i < this.state.attributes.length; i++) {

                // Setting primary attribute

                if (this.state.attributes[i].primary) {
                    this.setState({ primaryattribute: this.state.attributes[i].id });
                }

                // Setting mandatory attribute

                if (this.state.attributes[i].mandatory) {
                    this['mandatory' + this.state.attributes[i].id].setValue(true);
                }

                // Setting multivalue attribute

                if (this.state.attributes[i].multivalue) {
                    this['multivalue' + this.state.attributes[i].id].setValue(true);
                }
            }
        }
    }

    /**
     * Event that fires when a group label clicked.
     * 
     * @param   {String}    id      ID of the group
     */

    _toggleGroup = (id) => {

        // Getting the group

        for (let i = 0; i < this.entitycreateaccess.length; i++) {
            if (this.entitycreateaccess[i].id === id && this.entitycreateaccess[i].checkbox) {

                // Toggling the checkbox of the group

                this.entitycreateaccess[i].checkbox.toggle();
                break;
            }
        }
    }

    /**
     * Event that fires when an attribute label clicked.
     * 
     * @param   {String}    id      ID of the attribute
     */

    _toggleAttribute = (id) => {
        this['attr' + id].toggle();
    }

    /**
     * Event that fires when a mandatory label clicked.
     * 
     * @param   {String}    id      ID of the attribute
     */

    _toggleMandatory = (id) => {

        // Toggling the checkbox of the mandatory attribute

        this['mandatory' + id].toggle();
    }

    /**
     * Event that fires when a multivalue label clicked.
     * 
     * @param   {String}    id      ID of the attribute
     */

    _toggleMultivalue = (id) => {

        // Toggling the checkbox of the multivalue attribute

        this['multivalue' + id].toggle();
    }

    /**
     * Event that fires when an attribute toggled.
     * 
     * @param   {String}    id      ID of the attribute
     */

    _attributeToggled = (id) => {

        let attributes = this.state.attributes;
        let index = attributes.findIndex((item) => { return item.id === id;});

        if (attributes[index].assigned) {
            delete attributes[index].assigned;
            delete attributes[index].ordernr;
        } else {
            let ordernr = 0;

            attributes.forEach((attr) => {
                if (attr.ordernr > ordernr) {
                    ordernr = attr.ordernr; 
                }
            });

            attributes[index].assigned = true;
            attributes[index].ordernr = ordernr + 1;
        }

        this.setState({ attributes: attributes });
    }

    /**
     * Event that fires when a primary attribute selected.
     * 
     * @param   {String}    id      ID of the attribute
     */

    _onPrimaryAttributeSelected = (id) => {
        let attributes = this.state.attributes;
        let index = attributes.findIndex((item) => { return item.id === id;});

        for (let i = 0; i < attributes.length; i++) {
            attributes[i].primary = false;
        }

        attributes[index].primary = true;
        this['mandatory' + attributes[index].id].setValue(true);
        this['multivalue' + attributes[index].id].setValue(false);

        this.setState({ primaryattribute: attributes[index].id, attributes: attributes }, () => {
            for (let i = 0; i < attributes.length; i++) {
                this['primary' + attributes[i].id].setValue(attributes[i].primary);
            }
        });
    }

    /**
     * Event that fires when a primary attribute toggled.
     * 
     * @param   {String}    id      ID of the attribute
     */

    _togglePrimary = (id) => {
        this['primary' + id].toggle();
    }

    /**
     * Moves the specified attribute to the specified direction.
     * 
     * @param   {String}    id          ID of the attribute
     * @param   {Number}    direction   direction of the move
     */

    _moveAttribute = (id, direction) => {
        let attributes = this.state.attributes;
        let index = attributes.findIndex((item) => { return item.id === id;});
        let orderNr;

        if (direction === 1) {
            orderNr = attributes[index].ordernr - 1;
        } else {
            orderNr = attributes[index].ordernr + 1;
        }

        for (let i = 0; i < attributes.length; i++) {
            if (attributes[i].ordernr === orderNr) {
                attributes[i].ordernr = attributes[index].ordernr;
                break;
            }
        }

        attributes[index].ordernr = orderNr;

        this.setState({ attributes: attributes });
    }

    /** 
     * Event that fires when the component mounted.
     */

    componentDidMount() {
        
        // Showing the progress indicator

        this.props.onLoading(true);

        // Getting not assigned attributes

        Service.loadAttribute().then((attributes) => {
            attributes = JSON.parse(JSON.stringify(attributes));
            
            for (let i = 0; i < attributes.length; i++) {
                if (this.props.entitytype.attributes) {
                    for (let j = 0; j < this.props.entitytype.attributes.length; j++) {
                        if (attributes[i].id === this.props.entitytype.attributes[j].id) {
                            let attr = Object.assign({}, attributes[i], this.props.entitytype.attributes[j]);
                            attr.assigned = true;
                            attributes[i] = attr;
                            break;
                        }
                    }
                }
            }

            // Getting not assigned groups

            Service.loadGroup().then((groups) => {
                if (this.props.entitytype.entitycreateaccess) {
                    for (let j = 0; j < this.props.entitytype.entitycreateaccess.length; j++) {
                        for (let i = 0; i < groups.length; i++) {
                            if (groups[i].id === this.props.entitytype.entitycreateaccess[j].id) {
                                groups.splice(i, 1);
                                break;
                            }
                        }
                    }
                }

                // Storing the not assigned attributes and groups

                this.setState({ attributes: attributes, groups: groups });

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Refreshing the screen in case of new entity type mode

                if (!this.props.entitytype.id) {
                    this._refresh();
                }
            }).catch((error) => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /** 
     * Entity type view renderer.
     */

    render() {

        // Preparing

        this.entitycreateaccess = [];
        this.attributes = [];

        let isVisibleUpIcon = (id) => {
            let ordernr = 1000000;
            let ordernrId;

            this.state.attributes.forEach((attr) => {
                if (attr.ordernr < ordernr) {
                    ordernr = attr.ordernr; 
                    ordernrId = attr.id;
                }
            });

            return ordernrId !== id;
        };

        let isVisibleDownIcon = (id) => {
            let ordernr = 0;
            let ordernrId;

            this.state.attributes.forEach((attr) => {
                if (attr.ordernr > ordernr) {
                    ordernr = attr.ordernr; 
                    ordernrId = attr.id;
                }
            });

            return ordernrId !== id;
        };

        /**
         * Renders the entity creation access.
         * 
         * @param {Object}      group       group that has to render 
         * @param {Number}      index       index of the group
         * @param {Boolean}     checked     selected state of the creation access
         */

        let renderCreateAccess = (group, index, checked) => {
            return (
                <div key={index} className="entitytypeview-entitycreateaccess horizontal center">
                    <Checkbox ref={c => {this.entitycreateaccess.push({ id: group.id, checkbox: c })}} checked={checked} tooltip={group.name} />
                    <Label leftgap={8} text={group.name} onTouch={this._toggleGroup.bind(this, group.id)} />
                </div>
            );
        };

        /**
         * Attribute sorter.
         * 
         * @param   {Object}    a       attribute A
         * @param   {Object}    b       attribute B
         * @return  {Number}    result  comparison result
         */

        let sortByOrderNr = (a, b) => {
            if (a.ordernr < b.ordernr) {
                return -1;
            } else if (a.ordernr > b.ordernr) {
                return 1;
            } else {
                return 0;
            }                    
        };

        /**
         * Attribute sorter.
         * 
         * @param   {Object}    a       attribute A
         * @param   {Object}    b       attribute B
         * @return  {Number}    result  comparison result
         */

        let sortByAssignedAndOrderNr = (a, b) => {
            if (a.assigned && !b.assigned) {
                return -1;
            } else if (!a.assigned && b.assigned) {
                return 1;
            } else {
                if (a.ordernr < b.ordernr) {
                    return -1;
                } else if (a.ordernr > b.ordernr) {
                    return 1;
                } else {
                    return 0;
                }
            }
        };

        return (
            <div className="entitytypeview" style={{ height: (this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.usefullheight - 32) : (this.props.context.environment.screen.usefullheight - 64)) + 'px' }}>
                <div className="entitytypeview-scroll-container" style={{ height: (this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.usefullheight - 92) : (this.props.context.environment.screen.usefullheight - 120)) + 'px' }}>
                    
                    {/* Edit mode */}

                    { this.state.edit && this.props.entitytype && <div className="vertical">

                        {/* Entity type name */}

                        <Textbox ref={c => {this.name = c;}} label={this.props.context.locale.entitytype.field.name.label} tooltip={this.props.context.locale.entitytype.field.name.tooltip} comment={this.props.context.locale.entitytype.field.name.comment} placeholder={this.props.context.locale.entitytype.field.name.placeholder} labelposition="top" width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 64) : 320} isrequired />

                        {/* Creation access */}

                        <Label text={this.props.context.locale.entitytype.field.access.label} bottomgap={4} />

                        {/* Assigned groups */}

                        { this.props.entitytype.entitycreateaccess.map((item, index) => {
                            return renderCreateAccess(item, index, true);
                        })}

                        {/* Not assigned groups */}

                        { this.state.groups.map((item, index) => {
                            return renderCreateAccess(item, index, false);
                        })}

                        {/* Attributes */}

                        <Label text={this.props.context.locale.entitytype.field.attributes.label} topgap={8} bottomgap={4} />

                        { this.state.attributes.sort(sortByAssignedAndOrderNr).map((item, index) => 
                            <div key={item.id} className="entitytypeview-attribute">

                                {/* Attribute selector */}

                                <div className="entitytypeview-attributes horizontal center">
                                    <Checkbox ref={c => {this['attr' + item.id] = c}} checked={item.assigned !== undefined} tooltip={item.name} onChecked={this._attributeToggled.bind(this, item.id)} />
                                    <b><Label leftgap={8} text={item.name} onTouch={this._toggleAttribute.bind(this, item.id)} /></b>
                                </div>

                                <div style={{ display: item.assigned ? undefined : 'none' }}>

                                    {/* Main attribute */}

                                    <div className="entitytypeview-mainattribute horizontal center">
                                        <Checkbox ref={c => {this['primary' + item.id] = c}} checked={item.primary} tooltip={this.props.context.locale.entitytype.field.mainattribute.tooltip} disabled={['text', 'integer', 'float'].indexOf(item.type) < 0} onChecked={this._onPrimaryAttributeSelected.bind(this, item.id)} />
                                        <b><Label leftgap={8} text={this.props.context.locale.entitytype.field.mainattribute.label} onTouch={this._togglePrimary.bind(this, item.id)} /></b>
                                    </div>

                                    {/* Mandatory */}

                                    <div className="entitytypeview-mandatory horizontal center">
                                        <Checkbox ref={c => {this['mandatory' + item.id] = c}} disabled={this.state.primaryattribute === item.id || ['comment'].indexOf(item.type) >= 0} tooltip={this.props.context.locale.entitytype.field.required.tooltip} />
                                        <Label leftgap={8} text={this.props.context.locale.entitytype.field.required.label} onTouch={this._toggleMandatory.bind(this, item.id)} />
                                    </div>

                                    {/* Multivalue */}

                                    <div className="entitytypeview-multivalue horizontal center">
                                        <Checkbox ref={c => {this['multivalue' + item.id] = c}} disabled={this.state.primaryattribute === item.id || ['picture', 'checkbox', 'radio', 'user', 'comment'].indexOf(item.type) >= 0} tooltip={this.props.context.locale.entitytype.field.multivalue.tooltip} />
                                        <Label leftgap={8} text={this.props.context.locale.entitytype.field.multivalue.label} onTouch={this._toggleMultivalue.bind(this, item.id)} />
                                    </div>
                                </div>

                                {/* Attribute navigator */}

                                <div className="entitytypeview-attribute-navigator vertical" style={{ display: item.assigned ? undefined : 'none' }}>
                                    { isVisibleUpIcon(item.id) && <img src={UpIcon} alt="Move attribute up" className="entitytypeview-attribute-navigator-icon" title={this.props.context.locale.entitytype.attributenavigator.up} onClick={this._moveAttribute.bind(this, item.id, 1)}></img> }
                                    { isVisibleDownIcon(item.id) && <img src={DownIcon} alt="Move attribute up" className="entitytypeview-attribute-navigator-icon" title={this.props.context.locale.entitytype.attributenavigator.down} onClick={this._moveAttribute.bind(this, item.id, 2)}></img> }
                                </div>
                            </div>                        
                        )}
                    </div> }

                    {/* View mode */}

                    { !this.state.edit && <div className="vertical">

                        {/* Entity type name */}

                        <Label text={this.props.context.locale.entitytype.field.name.label} />
                        <div className="entitytypeview-value">{ this.props.entitytype.name }</div>

                        {/* Creation access */}

                        <Label text={this.props.context.locale.entitytype.field.access.label} topgap={16} />
                        { this.props.entitytype.entitycreateaccess.map((item, index) =>
                            <div key={index} className="entitytypeview-value">{item.name}</div>
                        )}

                        {/* Main attribute */}

                        <Label text={this.props.context.locale.entitytype.field.mainattribute.title} topgap={16} />
                        { this.props.entitytype.attributes.map((item, index) =>
                            item.primary && <div key={index} className="entitytypeview-value">{item.name}</div>
                        )}

                        {/* Attributes */}

                        <Label text={this.props.context.locale.entitytype.field.attributes.label} topgap={16} />
                        { this.props.entitytype.attributes.sort(sortByOrderNr).map((item, index) =>
                            !item.primary && <div key={index} className="entitytypeview-value">{item.name}</div>
                        )}
                    </div> }
                </div>

                {/* Buttons */}

                <div className="entitytypeview-buttons horizontal">

                    {/* Delete */}

                    { !this.state.edit && this.props.entitytype && <Button label={this.props.context.locale.entitytype.buttons.delete.label} tooltip={this.props.context.locale.entitytype.buttons.delete.tooltip} width={60} onTouch={this._delete} /> }

                    {/* Cancel */}

                    { this.state.edit && <Button label={this.props.context.locale.entitytype.buttons.cancel.label} tooltip={this.props.context.locale.entitytype.buttons.cancel.tooltip} width={60} onTouch={this._cancel} /> }

                    {/* Spacer */}

                    <div className="flex"></div>
                    
                    {/* Modify */}

                    { !this.state.edit && this.props.entitytype && <Button label={this.props.context.locale.entitytype.buttons.modify.label} tooltip={this.props.context.locale.entitytype.buttons.modify.tooltip} width={60} onTouch={this._modify} /> }

                    {/* Save */}

                    { this.state.edit && <Button label={this.props.context.locale.entitytype.buttons.save.label} tooltip={this.props.context.locale.entitytype.buttons.save.tooltip} width={60} onTouch={this._save} /> }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Entitytypeview/Entitytypeview.js